/**
 * Lobby adapter: wraps a Colyseus Room into the lobby surface consumed by
 * arena-handler.js / proximity-chat.js / movement.js.
 *
 * Features:
 *   • WebRTC signaling (`rtcOffer`/`rtcAnswer`/`rtcIce`) sent via `relayTo`
 *     so only the target peer receives it — O(1) instead of O(n) per packet.
 *   • Reconnect tokens persisted in sessionStorage so the existing
 *     `lobby.actions.reconnect()` call path actually recovers the player's
 *     team / kills within the server's 30 s grace window.
 */
import { Client } from 'colyseus.js';

const RECONNECT_STORAGE_KEY = 'arena.reconnectToken';

let _client = null;
function getClient(wsUrl) {
    if (!_client) _client = new Client(wsUrl);
    return _client;
}

function saveReconnectToken(room) {
    try {
        const token = room.reconnectionToken
            || (room.connection && room.connection.reconnectionToken);
        if (token) sessionStorage.setItem(RECONNECT_STORAGE_KEY, token);
    } catch (e) { /* ignore */ }
}
function loadReconnectToken() {
    try { return sessionStorage.getItem(RECONNECT_STORAGE_KEY); } catch { return null; }
}
function clearReconnectToken() {
    try { sessionStorage.removeItem(RECONNECT_STORAGE_KEY); } catch { /* ignore */ }
}

// Messages that should be sent point-to-point. The server's `relayTo` channel
// forwards only to `payload.target`, collapsing O(n) WebRTC chatter to O(1).
const TARGETED_TYPES = new Set(['rtcOffer', 'rtcAnswer', 'rtcIce']);

/** Wrap a Colyseus Room into the lobby object consumed by gameplay code. */
function wrapRoom(room, accountId, username) {
    const callbacks = { receiveAll: [], playerSync: [], disconnect: [], error: [] };

    room.onMessage('relay', (msg) => {
        for (const cb of callbacks.receiveAll) {
            try { cb(msg); } catch (e) { console.error('[colyseus-adapter] onReceiveAll cb threw', e); }
        }
    });

    room.onLeave((code) => {
        for (const cb of callbacks.disconnect) {
            try { cb(code); } catch (e) { console.error(e); }
        }
    });

    room.onError((code, message) => {
        for (const cb of callbacks.error) {
            try { cb(code, message); } catch (e) { console.error(e); }
        }
    });

    const fireSync = (event, sessionId) => {
        for (const cb of callbacks.playerSync) {
            try { cb({ event, sessionId }); } catch (e) { console.error(e); }
        }
    };

    room.state.players.onAdd((_p, sessionId) => fireSync('join', sessionId));
    room.state.players.onRemove((_p, sessionId) => fireSync('leave', sessionId));

    // Persist the reconnect token as soon as it's available.
    saveReconnectToken(room);

    const lobby = {
        session: {
            lobbyId: room.id,
            userId: room.sessionId,           // ephemeral session id
            accountId,                         // durable id, used by REST shims
            username,
        },
        actions: {
            listPlayers: async () => {
                // Schema-backed: includes server-authoritative team + kills.
                // Callers read both `name`/`username` and `sessionId`/`userId`
                // (arena-handler.js#L5061, leaderboard.js#L294) — expose both.
                const out = {};
                room.state.players.forEach((p, sessionId) => {
                    out[sessionId] = {
                        sessionId,
                        userId:   sessionId,
                        username: p.username,
                        name:     p.username,
                        team: p.team,
                        kills: p.kills,
                        deaths: p.deaths,
                        level: p.level,
                        prestige: p.prestige,
                    };
                });
                return out;
            },
            abandon: () => {
                clearReconnectToken();
                try { room.leave(true); } catch (e) {}
            },
            disconnect: () => {
                clearReconnectToken();
                try { room.leave(true); } catch (e) {}
            },
            // Reconnect using the token captured at join. Returns a lobby (the
            // existing call site in arena-handler#_clearStaleLobby treats a
            // truthy return as "session recovered, then drop it").
            reconnect: async () => {
                const token = loadReconnectToken();
                if (!token) return null;
                try {
                    const reroom = await _client.reconnect(token);
                    saveReconnectToken(reroom);
                    return wrapRoom(reroom, accountId, username);
                } catch (e) {
                    clearReconnectToken();
                    return null;
                }
            },
        },
        data: {
            sendToAll: (msg) => {
                if (!msg) return;
                try {
                    if (TARGETED_TYPES.has(msg.type) && msg.payload && msg.payload.target) {
                        room.send('relayTo', { target: msg.payload.target, message: msg });
                    } else {
                        room.send('relay', msg);
                    }
                } catch (e) {
                    console.warn('[colyseus-adapter] sendToAll failed', e);
                }
            },
            onReceiveAll: (cb) => { callbacks.receiveAll.push(cb); },
            sendToTeam: (msg) => { try { room.send('relay', msg); } catch (e) {} },
            onReceiveTeam: (cb) => { callbacks.receiveAll.push(cb); },
        },
        events: {
            onPlayerSync: (cb) => { callbacks.playerSync.push(cb); },
            onDisconnect: (cb) => { callbacks.disconnect.push(cb); },
            onError: (cb) => { callbacks.error.push(cb); },
            onMessage: () => {},
        },
        _room: room, // escape hatch for future direct schema reads
    };

    return lobby;
}

/**
 * Build the Arena.lobby façade. `getAuth` returns { token, accountId, name }.
 */
export function buildArenaShim({ wsUrl, getAuth }) {
    const client = getClient(wsUrl);
    let currentLobby = null;

    async function joinOrCreate({ room, team }) {
        const { token, accountId, name } = await getAuth();

        // Try a transparent reconnect first — recovers kills/team within the
        // server's 30 s grace window after a tab reload or backgrounding.
        const stash = loadReconnectToken();
        if (stash) {
            try {
                const reroom = await client.reconnect(stash);
                saveReconnectToken(reroom);
                currentLobby = wrapRoom(reroom, accountId, name);
                return currentLobby;
            } catch (e) {
                clearReconnectToken();
            }
        }

        const colyseusRoom = await client.joinOrCreate('arena', {
            room: room || 'arena',
            team: team || 'default',
            username: name,
            token,
        });
        currentLobby = wrapRoom(colyseusRoom, accountId, name);
        return currentLobby;
    }

    return {
        lobbies: async (_room) => {
            // Matchmaker handles capacity via joinOrCreate; returning [] forces
            // the existing client code into the createLobby branch (which calls
            // joinOrCreate) — exactly what we want.
            return [];
        },
        getLobby: async (_room) => currentLobby || null,
        joinLobby: async ({ room, lobby: _lobbyId, team }) => joinOrCreate({ room, team }),
        createLobby: async ({ room, team }) => joinOrCreate({ room, team }),
    };
}
